import { Box, Divider, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { colours } from "../../StyleConstants";

interface SectionContent {
    subTitle?: string;
    content: string;
    image?: string;
}

interface Section {
    title: string;
    link: string;
    content: SectionContent[];
}

const DUCFAQSection = () => {
    const [selectedSection, setSelectedSection] = useState<Section | null>(null);
    const sections: Section[] =  [
        {
            title: 'What should I do with data I have downloaded from NDA?',
            link: '#question1',
            content:  [
                { content: 'All ABCD data associated with an NDA DUC must be deleted no later than the NDA DUC expiration date.' },
            ]
        },
        {
            title: 'Previously, the DUC allowed multiple individuals at our institution to access the data. ' +
                'Will each person at my institution now require their own DUC?',
            link: '#question2',
            content:  [
                { content: 'While individuals can obtain their own DUCs, a group-level ' +
                        'DUC is recommended. This approach allows all recipients to be ' +
                        'listed together. A user dashboard provides functionality for ' +
                        'managing the addition or removal of recipients.' },
            ]
        },
        {
            title: 'Will trainees (e.g., graduate students, post-docs) have ' +
                'their own DUC?',
            link: '#question3',
            content:  [
                { content: 'It is recommended that trainees be included on an ' +
                        'investigator-led (group-level) DUC.' },
            ]
        },
        {
            title: 'Does the institutional signing official need to sign off for ' +
                'every individual that completes the DUC?',
            link: '#question4',
            content:  [
                { content: 'Yes, an institutional signing official must sign off on each ' +
                        'DUC in order for all recipients to gain access to the data. Only ' +
                        'one signature is needed  If all recipients are listed on the same ' +
                        'DUC.' },
            ]
        },
        {
            title: 'Is there a process to ensure faculty mentor approval for ' +
                'students (e.g., undergraduates, post-baccalaureate ' +
                'students)?',
            link: '#question5',
            content:  [
                { content: 'The group-level DUC facilitates this process as the lead ' +
                        'investigator will oversee trainees and others listed under the ' +
                        'DUC and is responsible for ensuring their compliance with its ' +
                        'terms and conditions.' },
            ]
        },
        {
            title: 'How can I access earlier ABCD data releases?',
            link: '#question6',
            content:  [
                { content: 'Prior ABCD data releases will be available on the NBDC ' +
                        'platform as downloadable packages. However, only the current ' +
                        'data release will be accessible via the NBDC interface.' },
            ]
        },
        {
            title: 'What steps are required if someone with a DUC moves to ' +
                'a new institution?',
            link: '#question7',
            content:  [
                { content: 'DUCs are not transferable between institutions. Recipients ' +
                        'must notify the NBDC platform at nbdc@mail.nih.gov when ' +
                        'moving to a new institution. A new DUC must then be submitted ' +
                        'with appropriate sponsorship from the new institution to retain ' +
                        'access. For group-level DUCs, the lead investigator may ' +
                        'designate another lead investigator at the original institution as ' +
                        'a replacement.' },
            ]
        },
        {
            title: 'Can we still use the 5.1 ABCD data release under the NDA ' +
                'DUC?',
            link: '#question8',
            content:  [
                { content: 'Yes, you may use the 5.1 data until your NDA DUC ' +
                        'expires. At that time, all data associated with that DUC must be ' +
                        'deleted.' },
            ]
        },
        {
            title: 'For publication transparency, will the DOI remain the ' +
                'same for the ABCD data releases?',
            link: '#question9',
            content:  [
                { content: 'A new DOI will be minted for the 5.1 data release. The old ' +
                        'DOI will direct users to the NDA page for past ABCD data ' +
                        'releases where users will be directed to the NBDC platform. On ' +
                        'the NBDC platform, a notification will indicate that the ABCD 5.1 ' +
                        'DOI has been updated to reflect best practices due to a location ' +
                        'change.' },
            ]
        },
        {
            title: 'If I obtain an A-DUC after receiving general DUC ' +
                'approval, which expiration date applies?',
            link: '#question10',
            content:  [
                { content: 'If you have both a general DUC and an A-DUC, they will ' +
                        'be linked so that the A-DUC expiration date matches the ' +
                        'expiration date of the general DUC.' },
            ]
        },
    ]

    const handleSectionClick = (section: Section) => {
        setSelectedSection(section);
    };

    const renderSections = () => {
        return (
            <Stack spacing={3}>
                {sections.map((section, index) => (
                    <Typography
                        key={index}
                        variant="body1"
                        component="a"
                        onClick={() => handleSectionClick(section)}
                        href={section.link}
                        sx={{
                            fontWeight: selectedSection?.link === section.link ? 'bold' : 'normal',
                            textDecoration: selectedSection?.link === section.link ? 'underline' : 'none'
                        }}
                    >
                        {section.title}
                    </Typography>
                ))}
            </Stack>
        )
    }

    const renderContent = () => {
        if (!selectedSection) {
            return (
                <Typography
                    variant="body1"
                    sx={{ mt: 2 }}
                >
                    Please select a question to view the content.
                </Typography>
            );
        }
        return (
            <Box id={selectedSection.link.substring(1)}>
                <Typography
                    variant="h4"
                    fontWeight="600"
                    fontSize="1.25rem"
                    lineHeight="1.75rem"
                    mb={2}
                >
                    {selectedSection.title}
                </Typography>
                {selectedSection.content.map((contentItem, idx) => (
                    <Box key={idx} mb={3}>
                        {contentItem.subTitle && (
                            <Typography
                                variant="h5"
                                fontWeight="500"
                                fontSize="1rem"
                                lineHeight="1.5rem"
                                mb={1}
                            >
                                {contentItem.subTitle}
                            </Typography>
                        )}
                        <Typography
                            variant="body1"
                            mb={2}
                        >
                            {contentItem.content}
                        </Typography>
                        {contentItem.image && (
                            <Box
                                component="img"
                                src={contentItem.image}
                                alt={selectedSection.title}
                                sx={{
                                    width: '100%',
                                    borderRadius: '8px',
                                    boxShadow: 1,
                                }}
                            />
                        )}
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <Stack
            direction="row"
            sx={{ backgroundColor: 'white',
                boxShadow:1,
                height: '100%',
                padding:'1.5rem'
            }}
            divider={<Divider orientation="vertical" flexItem />}
            spacing={2}
        >
            <Box sx={{ width: '40%', p: 2, overflowY: 'auto', maxHeight: '100vh' }}>
                <Typography
                    variant="h2"
                    fontWeight="600"
                    fontSize="1.5rem"
                    lineHeight="2rem"
                    color={colours.midnightBlue.blue}>
                    FAQs
                </Typography>
                {renderSections()}
            </Box>
            <Box sx={{ width: '60%', p: 2, overflowY: 'auto',maxHeight: '100vh' }}>
                {renderContent()}
            </Box>
        </Stack>
    )
}

export default DUCFAQSection;